import type { Schemas } from '#shopware'
import { useCart as swUseCart } from '@shopware/composables';
import { ApiClientError } from '@shopware/api-client'
import { createSharedComposable } from '@vueuse/core';
import { ref, readonly } from 'vue'
import { useContext, usePromotionStore } from "#imports";

const CART_LOCKED_RETRY_DELAY_MS = 300

function customUseCart() {
    const { addProducts, removeItem: swRemoveItem, ...coreUseCart } = swUseCart()
    const _recentlyAddedProductId = useContext<string | null>('recentlyAddedProductId')
    const _isCartWriteInFlight = ref(false)
    const { promotionData } = usePromotionStore()

    /**
     * Add doc here
     */
    function clearRecentlyAddedProductId(): void {
        _recentlyAddedProductId.value = null
    }

    async function addProduct(params: {
        id: string;
        quantity?: number;
        payload?: Schemas['AddToCartPayload']
    }): Promise<Schemas["Cart"]> {
        // Persist promotion attribution (from teaser click) onto the line item payload so it
        // survives into Shopware and is available for all subsequent cart/order GTM events.
        const mergedPayload: Schemas['AddToCartPayload'] = {
            ...params.payload,
            ...(promotionData.value != null && { ...promotionData.value }),
        }

        const addedProduct = addProducts([
            {
                id: params.id,
                quantity: params.quantity ?? 0,
                type: "product",
                ...(Object.keys(mergedPayload).length > 0 && { payload: mergedPayload })
            },
        ]);

        _recentlyAddedProductId.value = params.id

        return addedProduct
    }

    /*
     * The cart is a single server-side resource, so overlapping writes collide: Shopware
     * answers 409 while the cart is locked, and a second delete of the same line item gets
     * 404. Both are reachable from CartListItem, where the trash button is not disabled
     * during the request and quantity changes are debounced by 1s.
     *
     * The flag is app-wide on purpose (this composable is shared) so callers can block
     * further writes while one is in flight.
     */
    async function attemptRemove(lineItem: Schemas['LineItem'], mayRetry: boolean): Promise<Schemas['Cart']> {
        try {
            return await swRemoveItem(lineItem)
        } catch (e: unknown) {
            if (!(e instanceof ApiClientError)) {
                throw e
            }

            // "locked due to concurrent write operation. Please try again later" -- exactly
            // one retry, so a genuinely stuck lock is not hammered in a loop.
            if (e.status === 409 && mayRetry) {
                await new Promise(resolve => setTimeout(resolve, CART_LOCKED_RETRY_DELAY_MS))
                return await attemptRemove(lineItem, false)
            }

            // The line item is already gone, which is what the caller asked for. Resync so
            // the rendered cart matches the server, then report it as the success it is.
            if (e.details?.errors?.[0]?.code === 'CHECKOUT__CART_LINE_ITEM_NOT_FOUND') {
                return await coreUseCart.refreshCart()
            }

            throw e
        }
    }

    async function removeItem(lineItem: Schemas['LineItem']): Promise<Schemas['Cart']> {
        _isCartWriteInFlight.value = true
        try {
            return await attemptRemove(lineItem, true)
        } finally {
            _isCartWriteInFlight.value = false
        }
    }

    return {
        ...coreUseCart,
        clearRecentlyAddedProductId,
        addProduct,
        removeItem,
        isCartWriteInFlight: readonly(_isCartWriteInFlight),
        recentlyAddedProductId: _recentlyAddedProductId
    }
}

export const useCart = createSharedComposable(customUseCart);